import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/** Paid selections are real Shopify variants emitted by Liquid, never amounts from JSON. */
export function buildCartItems(formData, extras) {
  if (!extras.length) return formData;
  if (extras.length > 16) throw new Error('Too many riding extras');

  const mainId = String(formData.get('id') || '');
  const mainQuantity = Number(formData.get('quantity') || 1);
  if (!validVariantId(mainId) || !Number.isSafeInteger(mainQuantity) || mainQuantity < 1) {
    throw new Error('Invalid main product');
  }

  const result = new FormData();
  for (const [key, value] of formData) {
    if (key !== 'id' && key !== 'quantity' && key !== 'selling_plan' && !key.startsWith('properties[')) result.append(key, value);
  }
  result.set('items[0][id]', mainId);
  result.set('items[0][quantity]', String(mainQuantity));
  if (formData.get('selling_plan')) result.set('items[0][selling_plan]', String(formData.get('selling_plan')));
  for (const [key, value] of formData) {
    if (key.startsWith('properties[') && key.endsWith(']') && value) {
      result.append(`items[0][properties][${key.slice(11, -1)}]`, value);
    }
  }

  extras.forEach(({ id, quantity, followMain, design }, index) => {
    if (!validVariantId(String(id)) || !Number.isSafeInteger(quantity) || quantity < 1) {
      throw new Error('Invalid riding extra');
    }
    result.set(`items[${index + 1}][id]`, String(id));
    result.set(`items[${index + 1}][quantity]`, String(quantity));
    if (followMain) {
      result.set(`items[${index + 1}][parent_id]`, mainId);
      result.set(`items[${index + 1}][properties][_riding_upgrade]`, 'true');
      result.set('items[0][properties][_riding_configured]', 'true');
    } else if (design) {
      if (typeof design !== 'string' || design.length > 200) throw new Error('Invalid matching design');
      result.set(`items[${index + 1}][properties][Matching design]`, design);
    }
  });
  const linkedIndices = extras.flatMap((extra, index) => extra.followMain ? [index + 1] : []);
  Object.defineProperty(result, 'syncRidingQuantities', {
    value(quantity) {
      if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Invalid main quantity');
      for (const index of linkedIndices) result.set(`items[${index}][quantity]`, String(quantity));
    },
  });
  return result;
}

function validVariantId(id) {
  return /^\d+$/.test(id) && Number.isSafeInteger(Number(id)) && Number(id) > 0;
}

/** Native prices are integer cents. Matching gear keeps its own quantity. */
export function calculateTotal(mainPrice, mainQuantity, extras = []) {
  const validPrice = (value) => Number.isSafeInteger(value) && value >= 0;
  const validQuantity = (value) => Number.isSafeInteger(value) && value > 0;
  if (!validPrice(mainPrice) || !validQuantity(mainQuantity)) throw new Error('Invalid product total');
  const baseTotal = mainPrice * mainQuantity;
  if (!Number.isSafeInteger(baseTotal)) throw new Error('Invalid product total');
  if (!Array.isArray(extras)) throw new Error('Invalid extras');
  const total = extras.reduce((sum, extra) => {
    if (!extra || !validPrice(extra.price) || !validQuantity(extra.quantity)) throw new Error('Invalid extra total');
    const extraTotal = extra.price * extra.quantity;
    if (!Number.isSafeInteger(extraTotal)) throw new Error('Invalid extra total');
    const nextSum = sum + extraTotal;
    if (!Number.isSafeInteger(nextSum)) throw new Error('Invalid product total');
    return nextSum;
  }, baseTotal);
  if (!Number.isSafeInteger(total)) throw new Error('Invalid product total');
  return total;
}

class NarsonRidingOptions extends Component {
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('change', () => this.handleChange(), { signal: this.#controller.signal });
    for (const eventName of ['input', 'change']) {
      this.closest('form')?.addEventListener(eventName, (event) => {
        if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') this.handleChange();
      }, { signal: this.#controller.signal });
    }
    this.closest('form')?.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.handleChange(), { signal: this.#controller.signal });
    this.#enableChoices();
    this.handleChange();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#enableChoices();
    this.handleChange();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  #enableChoices() {
    for (const choice of this.querySelectorAll('option[data-upgrade-id]')) choice.disabled = false;
    for (const toggle of this.querySelectorAll('[data-addon-toggle]')) toggle.disabled = false;
  }

  handleChange() {
    const form = this.closest('product-form-component');
    const variant = form?.querySelector('[ref="variantId"]');
    const nativeValues = JSON.parse(variant?.dataset?.variantOptions || '[]');
    // Configured forms use Add to cart so custom properties cannot be bypassed by a wallet.
    let paid = Boolean(this.querySelector('select[name^="properties["], textarea[name^="properties["], input[data-addon-checkbox]:checked, [data-addon-card].is-selected'));
    const propSelects = Array.from(this.querySelectorAll('select[name^="properties["]'));
    for (const select of propSelects) {
      if (select.selectedOptions[0]?.dataset.upgradeId) paid = true;
    }
    for (const chk of this.querySelectorAll('[data-addon-checkbox]')) {
      if (chk.checked) paid = true;
    }
    for (const card of this.querySelectorAll('[data-addon-card]')) {
      const checked = card.querySelector('[data-addon-toggle]')?.checked === true;
      const picker = card.querySelector('[data-addon-variant]');
      if (picker) {
        picker.disabled = !checked;
        picker.required = checked;
      }
      card.classList.toggle('is-selected', checked);
      if (checked) paid = true;
    }
    for (const measurements of this.querySelectorAll('[data-measurement-fields]')) {
      const card = measurements.dataset.gearMeasurements ? measurements.closest('[data-addon-card]') : null;
      const trigger = card
        ? card.querySelector('[data-addon-toggle]')?.checked && card.querySelector('[data-addon-variant]')?.selectedOptions[0]?.dataset.customSize === 'true'
        : measurements.dataset.nativeTrigger === 'true'
          ? nativeValues.some((value) => String(value).toLowerCase() === measurements.dataset.triggerValue?.toLowerCase())
          : propSelects.some((select) => select.name === `properties[${measurements.dataset.triggerName}]` && select.value.trim() === measurements.dataset.triggerValue?.trim());
      const active = Boolean(trigger);
      if (!active && !measurements.hidden) {
        for (const input of measurements.querySelectorAll('[data-measurement-input]')) input.value = '';
      }
      measurements.hidden = !active;
      const unit = measurements.querySelector('[data-measurement-unit]');
      if (unit) unit.disabled = !active;
      if (unit?.dataset.previousUnit && unit.dataset.previousUnit !== unit.value) {
        for (const input of measurements.querySelectorAll('[data-measurement-input]')) input.value = '';
      }
      if (unit) unit.dataset.previousUnit = unit.value;
      for (const input of measurements.querySelectorAll('[data-measurement-input]')) {
        input.disabled = !active;
        input.required = active;
        if (active) {
          const factor = unit?.value === 'cm' ? 2.54 : 1;
          input.min = String(Math.ceil(Number(input.dataset.minIn) * factor * 10) / 10);
          input.max = String(Math.floor(Number(input.dataset.maxIn) * factor * 10) / 10);
        }
      }
    }
    // Dynamic checkout submits only the main variant. Hide it while extras are selected.
    const checkout = form?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (checkout && paid) {
      checkout.hidden = true;
      checkout.dataset.ridingOptionsHidden = 'true';
    } else if (checkout?.dataset.ridingOptionsHidden) {
      delete checkout.dataset.ridingOptionsHidden;
      checkout.hidden = !!form?.querySelector('add-to-cart-component button[disabled]');
    }
    const output = this.querySelector('[data-configured-total]');
    if (output && variant) {
      const quantity = Number(form?.querySelector('[name="quantity"]')?.value || 1);
      const basePrice = Number(variant.dataset.price || this.dataset.basePrice || 0);
      const rawCompare = Number(variant.dataset.comparePrice || this.dataset.comparePrice || 0);
      const comparePrice = rawCompare > basePrice ? rawCompare : Math.round(basePrice * 1.25);
      const extras = [];
      const seenOptions = new Set();
      for (const select of this.querySelectorAll('select[name^="properties["]')) {
        const choice = select.selectedOptions[0];
        if (choice && Number(choice.dataset.price) > 0) {
          extras.push({ price: Number(choice.dataset.price), quantity });
          seenOptions.add(choice);
        }
      }
      for (const choice of this.querySelectorAll('option[data-upgrade-id]:checked')) {
        if (!seenOptions.has(choice) && Number(choice.dataset.price) > 0) {
          extras.push({ price: Number(choice.dataset.price), quantity });
          seenOptions.add(choice);
        }
      }
      for (const chk of this.querySelectorAll('[data-addon-checkbox]:checked')) {
        const p = Number(chk.dataset.addonPrice || 0);
        if (p > 0) {
          extras.push({ price: p, quantity: 1 });
        }
      }
      for (const card of this.querySelectorAll('[data-addon-card]')) {
        if (!card.querySelector('[data-addon-checkbox]')) {
          const choice = card.querySelector('[data-addon-variant]')?.selectedOptions[0];
          if (card.querySelector('[data-addon-toggle]')?.checked && choice?.value && Number(choice.dataset.price) > 0) {
            extras.push({ price: Number(choice.dataset.price), quantity: Number(choice.dataset.quantity || 1) });
          }
        }
      }
      try {
        const total = calculateTotal(basePrice, quantity, extras);
        const regularTotal = calculateTotal(comparePrice, quantity, extras);
        const savedAmount = Math.max(0, regularTotal - total);
        const savedPercent = regularTotal > 0 ? Math.round((savedAmount / regularTotal) * 100) : 0;
        const currencyCode = (this.dataset.currency || 'USD').trim().toUpperCase();
        const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: currencyCode,
        });

        output.textContent = formatter.format(total / 100);

        const regularOutput = this.querySelector('[data-configured-regular]');
        if (regularOutput) {
          regularOutput.textContent = formatter.format(regularTotal / 100);
          regularOutput.style.display = regularTotal > total ? 'inline' : 'none';
        }

        const saveOutput = this.querySelector('[data-configured-save]');
        if (saveOutput) {
          if (savedAmount > 0) {
            saveOutput.textContent = `SAVE ${formatter.format(savedAmount / 100)} (${savedPercent}%)`;
            saveOutput.style.display = 'inline-flex';
          } else {
            saveOutput.style.display = 'none';
          }
        }

        // Synchronize with parent buy-box price row and mobile sticky bar
        const buyBox = this.closest('.motojp-buy-box, .product-details, .motojp-pdp-top-grid__buybox, .product-information');
        if (buyBox) {
          const mainPrice = buyBox.querySelector('[data-narson-price]');
          if (mainPrice) mainPrice.textContent = formatter.format(total / 100);
          const mainCompare = buyBox.querySelector('[data-narson-compare]');
          if (mainCompare) {
            mainCompare.textContent = formatter.format(regularTotal / 100);
            mainCompare.hidden = regularTotal <= total;
          }
          const mainSave = buyBox.querySelector('[data-narson-save-badge]');
          if (mainSave) {
            if (savedAmount > 0) {
              mainSave.textContent = `SAVE ${formatter.format(savedAmount / 100)} (${savedPercent}%)`;
              mainSave.hidden = false;
            } else {
              mainSave.hidden = true;
            }
          }
        }
      } catch {
        output.textContent = this.dataset.errorMessage;
      }
    }
  }

  buildCartFormData(formData) {
    for (const measurements of this.querySelectorAll('[data-measurement-fields]')) {
      if (measurements.hidden) continue;
      const factor = measurements.querySelector('[data-measurement-unit]')?.value === 'cm' ? 2.54 : 1;
      for (const input of measurements.querySelectorAll('[data-measurement-input]')) {
        const value = Number(input.value);
        const min = Number(input.dataset.minIn) * factor;
        const max = Number(input.dataset.maxIn) * factor;
        if (input.disabled || !Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= min || value <= 0 || value < min || value > max) {
          throw new Error('Invalid measurement');
        }
      }
    }
    const mainQuantity = Number(formData.get('quantity') || 1);
    const extras = [];
    for (const select of this.querySelectorAll('select[name^="properties["]')) {
      const choice = select.selectedOptions[0];
      if (!choice || choice.disabled) throw new Error('Unavailable riding option');
      if (choice.dataset.upgradeId) extras.push({ id: choice.dataset.upgradeId, quantity: mainQuantity, followMain: true });
    }
    for (const card of this.querySelectorAll('[data-addon-card]')) {
      if (!card.querySelector('[data-addon-toggle]')?.checked) continue;
      const select = card.querySelector('[data-addon-variant]');
      const choice = select?.selectedOptions[0];
      if (!choice?.value || choice.disabled || select.disabled) throw new Error('Choose available matching gear');
      if (/^\d+$/.test(choice.value)) {
        const quantity = Number(choice.dataset.quantity || 1);
        extras.push({ id: choice.value, quantity, design: this.dataset.productTitle });
      }
    }
    return buildCartItems(formData, extras);
  }
}

if (!customElements.get('narson-riding-options')) {
  customElements.define('narson-riding-options', NarsonRidingOptions);
}
